import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { ArrowLeft, Save, AlertCircle } from "lucide-react";
import bookingService, {
  Booking,
  UpdateBookingRequest,
} from "../../services/bookingService";

const AUTO_REASON_HBR = "Hotel booking reference updated.";
const AUTO_REASON_HCN = "Hotel confirmation number updated.";

const EditBooking: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [booking, setBooking] = useState<Booking | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<UpdateBookingRequest>({
    change_reason: "",
  });

  useEffect(() => {
    if (id) {
      fetchBooking();
    }
  }, [id]);

  // Auto-calculate total amount whenever financial fields change
  useEffect(() => {
    // Ensure all values are numbers, not strings
    const baseAmount = Number(formData.base_amount) || 0;
    const markupAmount = Number(formData.markup_amount) || 0;
    const serviceChargeAmount = Number(formData.service_charge_amount) || 0;
    const calculatedTotal = baseAmount + markupAmount + serviceChargeAmount;

    console.log("Financial calculation:", {
      baseAmount,
      markupAmount,
      serviceChargeAmount,
      calculatedTotal,
      currentTotal: formData.total_amount,
    });

    if (Number(formData.total_amount) !== calculatedTotal) {
      setFormData((prev) => ({
        ...prev,
        total_amount: calculatedTotal,
      }));
    }
  }, [
    formData.base_amount,
    formData.markup_amount,
    formData.service_charge_amount,
  ]);

  const enforceStatusForHotelReferences = (
    data: UpdateBookingRequest
  ): UpdateBookingRequest => {
    const confirmation = (data.hotel_confirmation_number ?? "")
      .toString()
      .trim();
    const bookingRef = (data.hotel_booking_ref_number ?? "").toString().trim();

    if (confirmation) {
      data.booking_status = "confirmed";
    } else if (bookingRef) {
      data.booking_status = "booked (HCN Pending)";
    }

    return data;
  };

  const fetchBooking = async () => {
    try {
      setLoading(true);
      const response = await bookingService.getBookingById(parseInt(id!));
      setBooking(response.data);
      const baseAmount = Number(response.data.base_amount) || 0;
      const markupAmount = Number(response.data.markup_amount) || 0;
      const serviceChargeAmount =
        Number(response.data.service_charge_amount) || 0;
      const calculatedTotal = baseAmount + markupAmount + serviceChargeAmount;

      setFormData(
        enforceStatusForHotelReferences({
          booking_status: response.data.booking_status,
          payment_status: response.data.payment_status,
          guest_first_name: response.data.guest_first_name || "",
          guest_last_name: response.data.guest_last_name || "",
          guest_email: response.data.guest_email || "",
          guest_phone: response.data.guest_phone || "",
          guest_nationality: response.data.guest_nationality || "",
          guest_address: response.data.guest_address || "",
          checkin_date: response.data.checkin_date
            ? response.data.checkin_date.split("T")[0]
            : "",
          checkout_date: response.data.checkout_date
            ? response.data.checkout_date.split("T")[0]
            : "",
          room_plan_id: response.data.room_plan_id,
          base_amount: baseAmount,
          markup_amount: markupAmount,
          service_charge_amount: serviceChargeAmount,
          total_amount: calculatedTotal,
          notes: response.data.notes || "",
          hotel_booking_ref_number:
            response.data.hotel_booking_ref_number || "",
          hotel_confirmation_number:
            response.data.hotel_confirmation_number || "",
          change_reason: "",
        })
      );
    } catch (err) {
      setError("Failed to fetch booking details");
      console.error("Error fetching booking details:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (
    field: keyof UpdateBookingRequest,
    value: string | number | null
  ) => {
    setFormData((prev) => {
      const newData = {
        ...prev,
        [field]: value,
      };

      if (
        field === "hotel_booking_ref_number" &&
        (!prev.change_reason?.trim() ||
          prev.change_reason === AUTO_REASON_HBR ||
          prev.change_reason === AUTO_REASON_HCN)
      ) {
        newData.change_reason = AUTO_REASON_HBR;
      }

      if (
        field === "hotel_confirmation_number" &&
        (!prev.change_reason?.trim() ||
          prev.change_reason === AUTO_REASON_HBR ||
          prev.change_reason === AUTO_REASON_HCN)
      ) {
        newData.change_reason = AUTO_REASON_HCN;
      }

      // Auto-calculate total amount when financial fields change
      if (
        field === "base_amount" ||
        field === "markup_amount" ||
        field === "service_charge_amount"
      ) {
        // Get the current values, using the new value for the field being changed
        const baseAmount =
          field === "base_amount"
            ? Number(value) || 0
            : Number(prev.base_amount) || 0;
        const markupAmount =
          field === "markup_amount"
            ? Number(value) || 0
            : Number(prev.markup_amount) || 0;
        const serviceChargeAmount =
          field === "service_charge_amount"
            ? Number(value) || 0
            : Number(prev.service_charge_amount) || 0;

        newData.total_amount = baseAmount + markupAmount + serviceChargeAmount;
      }

      return enforceStatusForHotelReferences(newData);
    });
  };

  const trimmedHotelBookingRef = (formData.hotel_booking_ref_number ?? "")
    .toString()
    .trim();
  const trimmedHotelConfirmation = (formData.hotel_confirmation_number ?? "")
    .toString()
    .trim();
  const isHotelBookingRefRequired =
    formData.booking_status === "booked (HCN Pending)";
  const isHotelConfirmationRequired = formData.booking_status === "confirmed";

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.change_reason?.trim()) {
      setError("Change reason is required");
      return;
    }

    if (isHotelBookingRefRequired && !trimmedHotelBookingRef) {
      setError(
        "Hotel booking reference is required for Booked (HCN Pending) status."
      );
      return;
    }

    if (isHotelConfirmationRequired && !trimmedHotelConfirmation) {
      setError("Hotel confirmation number is required for Confirmed status.");
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const payload: UpdateBookingRequest = {
        ...formData,
        hotel_booking_ref_number:
          formData.hotel_booking_ref_number !== undefined
            ? trimmedHotelBookingRef || null
            : undefined,
        hotel_confirmation_number:
          formData.hotel_confirmation_number !== undefined
            ? trimmedHotelConfirmation || null
            : undefined,
      };

      const result = await bookingService.updateBooking(parseInt(id!), payload);

      if (result.success) {
        navigate(`/bookings/${id}`);
      } else {
        setError("Failed to update booking");
      }
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to update booking");
      console.error("Error updating booking:", err);
    } finally {
      setSaving(false);
    }
  };

  const isSaveDisabled =
    saving ||
    !formData.change_reason?.trim() ||
    (isHotelBookingRefRequired && !trimmedHotelBookingRef) ||
    (isHotelConfirmationRequired && !trimmedHotelConfirmation);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-950 p-6">
        <div className="max-w-4xl mx-auto">
          <div className="text-white">Loading...</div>
        </div>
      </div>
    );
  }

  if (error && !booking) {
    return (
      <div className="min-h-screen bg-gray-950 p-6">
        <div className="max-w-4xl mx-auto">
          <div className="text-red-400">{error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-950 p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(`/bookings/${id}`)}
              className="border-gray-700 text-gray-300 hover:bg-gray-800"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Booking
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-white">Edit Booking</h1>
              <p className="text-gray-400">
                Booking Reference: {booking?.booking_reference}
              </p>
            </div>
          </div>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-900/20 border border-red-500/50 rounded-lg flex items-center space-x-2">
            <AlertCircle className="w-5 h-5 text-red-400" />
            <span className="text-red-400">{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Booking Status & Payment Status */}
          <Card className="bg-gray-900 border-gray-800">
            <CardHeader>
              <CardTitle className="text-white">Status Information</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-4">
                <Label htmlFor="booking_status" className="text-gray-300">
                  Booking Status
                </Label>
                <Select
                  value={formData.booking_status}
                  onValueChange={(value) =>
                    handleInputChange("booking_status", value)
                  }
                >
                  <SelectTrigger className="bg-gray-800 border-gray-700 text-white w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="confirmed">Confirmed</SelectItem>
                    <SelectItem value="booked (HCN Pending)">
                      Booked (HCN Pending)
                    </SelectItem>
                    <SelectItem value="pending (HBR Pending)">
                      Pending (HBR Pending)
                    </SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                    <SelectItem value="no_show">No Show</SelectItem>
                    <SelectItem value="checked_in">Checked In</SelectItem>
                    <SelectItem value="checked_out">Checked Out</SelectItem>
                  </SelectContent>
                </Select>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label
                      htmlFor="hotel_booking_ref_number"
                      className="text-gray-300"
                    >
                      Hotel Booking Reference
                    </Label>
                    <Input
                      id="hotel_booking_ref_number"
                      value={formData.hotel_booking_ref_number || ""}
                      onChange={(e) =>
                        handleInputChange(
                          "hotel_booking_ref_number",
                          e.target.value
                        )
                      }
                      className="bg-gray-800 border-gray-700 text-white"
                      maxLength={15}
                      placeholder="e.g. HB12345"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Maximum 15 characters.
                    </p>
                    {isHotelBookingRefRequired && !trimmedHotelBookingRef && (
                      <p className="text-xs text-amber-400 mt-1">
                        Required when booking status is Booked (HCN Pending).
                      </p>
                    )}
                  </div>
                  <div>
                    <Label
                      htmlFor="hotel_confirmation_number"
                      className="text-gray-300"
                    >
                      Hotel Confirmation Number
                    </Label>
                    <Input
                      id="hotel_confirmation_number"
                      value={formData.hotel_confirmation_number || ""}
                      onChange={(e) =>
                        handleInputChange(
                          "hotel_confirmation_number",
                          e.target.value
                        )
                      }
                      className="bg-gray-800 border-gray-700 text-white"
                      maxLength={15}
                      placeholder="e.g. CN67890"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Maximum 15 characters.
                    </p>
                    {isHotelConfirmationRequired &&
                      !trimmedHotelConfirmation && (
                        <p className="text-xs text-amber-400 mt-1">
                          Required when booking status is Confirmed.
                        </p>
                      )}
                  </div>
                </div>
              </div>
              <div className="space-y-4">
                <Label htmlFor="payment_status" className="text-gray-300">
                  Payment Status
                </Label>
                <Select
                  value={formData.payment_status}
                  onValueChange={(value) =>
                    handleInputChange("payment_status", value)
                  }
                >
                  <SelectTrigger className="bg-gray-800 border-gray-700 text-white w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="paid">Paid</SelectItem>
                    <SelectItem value="failed">Failed</SelectItem>
                    <SelectItem value="refunded">Refunded</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Guest Information */}
          <Card className="bg-gray-900 border-gray-800">
            <CardHeader>
              <CardTitle className="text-white">Guest Information</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="guest_first_name" className="text-gray-300">
                  First Name
                </Label>
                <Input
                  id="guest_first_name"
                  value={formData.guest_first_name}
                  onChange={(e) =>
                    handleInputChange("guest_first_name", e.target.value)
                  }
                  className="bg-gray-800 border-gray-700 text-white"
                />
              </div>
              <div>
                <Label htmlFor="guest_last_name" className="text-gray-300">
                  Last Name
                </Label>
                <Input
                  id="guest_last_name"
                  value={formData.guest_last_name}
                  onChange={(e) =>
                    handleInputChange("guest_last_name", e.target.value)
                  }
                  className="bg-gray-800 border-gray-700 text-white"
                />
              </div>
              <div>
                <Label htmlFor="guest_email" className="text-gray-300">
                  Email
                </Label>
                <Input
                  id="guest_email"
                  type="email"
                  value={formData.guest_email}
                  onChange={(e) =>
                    handleInputChange("guest_email", e.target.value)
                  }
                  className="bg-gray-800 border-gray-700 text-white"
                />
              </div>
              <div>
                <Label htmlFor="guest_phone" className="text-gray-300">
                  Phone
                </Label>
                <Input
                  id="guest_phone"
                  value={formData.guest_phone}
                  onChange={(e) =>
                    handleInputChange("guest_phone", e.target.value)
                  }
                  className="bg-gray-800 border-gray-700 text-white"
                />
              </div>
              <div>
                <Label htmlFor="guest_nationality" className="text-gray-300">
                  Nationality
                </Label>
                <Input
                  id="guest_nationality"
                  value={formData.guest_nationality}
                  onChange={(e) =>
                    handleInputChange("guest_nationality", e.target.value)
                  }
                  className="bg-gray-800 border-gray-700 text-white"
                />
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="guest_address" className="text-gray-300">
                  Address
                </Label>
                <Textarea
                  id="guest_address"
                  value={formData.guest_address}
                  onChange={(e) =>
                    handleInputChange("guest_address", e.target.value)
                  }
                  className="bg-gray-800 border-gray-700 text-white"
                  rows={3}
                />
              </div>
            </CardContent>
          </Card>

          {/* Dates & Room */}
          <Card className="bg-gray-900 border-gray-800">
            <CardHeader>
              <CardTitle className="text-white">
                Dates & Room Information
              </CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="checkin_date" className="text-gray-300">
                  Check-in Date
                </Label>
                <Input
                  id="checkin_date"
                  type="date"
                  value={formData.checkin_date}
                  onChange={(e) =>
                    handleInputChange("checkin_date", e.target.value)
                  }
                  className="bg-gray-800 border-gray-700 text-white"
                />
              </div>
              <div>
                <Label htmlFor="checkout_date" className="text-gray-300">
                  Check-out Date
                </Label>
                <Input
                  id="checkout_date"
                  type="date"
                  value={formData.checkout_date}
                  onChange={(e) =>
                    handleInputChange("checkout_date", e.target.value)
                  }
                  className="bg-gray-800 border-gray-700 text-white"
                />
              </div>
            </CardContent>
          </Card>

          {/* Financial Information */}
          <Card className="bg-gray-900 border-gray-800">
            <CardHeader>
              <CardTitle className="text-white">
                Financial Information
              </CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="base_amount" className="text-gray-300">
                  Base Amount
                </Label>
                <Input
                  id="base_amount"
                  type="number"
                  step="0.01"
                  value={formData.base_amount}
                  onChange={(e) =>
                    handleInputChange(
                      "base_amount",
                      parseFloat(e.target.value) || 0
                    )
                  }
                  className="bg-gray-800 border-gray-700 text-white"
                />
              </div>
              <div>
                <Label htmlFor="markup_amount" className="text-gray-300">
                  Markup Amount
                </Label>
                <Input
                  id="markup_amount"
                  type="number"
                  step="0.01"
                  value={formData.markup_amount}
                  onChange={(e) =>
                    handleInputChange(
                      "markup_amount",
                      parseFloat(e.target.value) || 0
                    )
                  }
                  className="bg-gray-800 border-gray-700 text-white"
                />
              </div>
              <div>
                <Label
                  htmlFor="service_charge_amount"
                  className="text-gray-300"
                >
                  Service Charge
                </Label>
                <Input
                  id="service_charge_amount"
                  type="number"
                  step="0.01"
                  value={formData.service_charge_amount}
                  onChange={(e) =>
                    handleInputChange(
                      "service_charge_amount",
                      parseFloat(e.target.value) || 0
                    )
                  }
                  className="bg-gray-800 border-gray-700 text-white"
                />
              </div>
              <div>
                <Label htmlFor="total_amount" className="text-gray-300">
                  Total Amount (Auto-calculated)
                </Label>
                <Input
                  id="total_amount"
                  type="number"
                  step="0.01"
                  value={formData.total_amount}
                  readOnly
                  className="bg-gray-700 border-gray-600 text-white cursor-not-allowed"
                />
              </div>
            </CardContent>
          </Card>

          {/* Notes */}
          <Card className="bg-gray-900 border-gray-800">
            <CardHeader>
              <CardTitle className="text-white">Notes</CardTitle>
            </CardHeader>
            <CardContent>
              <div>
                <Label htmlFor="notes" className="text-gray-300">
                  Notes
                </Label>
                <Textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => handleInputChange("notes", e.target.value)}
                  className="bg-gray-800 border-gray-700 text-white"
                  rows={4}
                  placeholder="Add any notes about this booking..."
                />
              </div>
            </CardContent>
          </Card>

          {/* Change Reason */}
          <Card className="bg-gray-900 border-gray-800">
            <CardHeader>
              <CardTitle className="text-white">Change Reason *</CardTitle>
              <CardDescription className="text-gray-400">
                Please provide a reason for these changes (required)
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div>
                <Label htmlFor="change_reason" className="text-gray-300">
                  Reason for Changes
                </Label>
                <Textarea
                  id="change_reason"
                  value={formData.change_reason}
                  onChange={(e) =>
                    handleInputChange("change_reason", e.target.value)
                  }
                  className="bg-gray-800 border-gray-700 text-white"
                  rows={3}
                  placeholder="Explain why these changes are being made..."
                  required
                />
              </div>
            </CardContent>
          </Card>

          {/* Submit Button */}
          <div className="flex justify-end space-x-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate(`/bookings/${id}`)}
              className="border-gray-700 text-gray-300 hover:bg-gray-800"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSaveDisabled}
              className={`${
                isSaveDisabled
                  ? "bg-blue-600/50 text-white/70 cursor-not-allowed"
                  : "bg-blue-600 hover:bg-blue-700 text-white"
              }`}
            >
              {saving ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save Changes
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditBooking;
